import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ClippyHost } from '../../clippy/react/ClippyHost';
import { NipeIntroBalloon } from './NipeIntroBalloon';
import { useFeatureFlags } from '../../lib/tenant/useFeatureFlags';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import { useRepancyRole } from '../../lib/auth/useRepancyRole';
import { useWindowContext } from '../desktop/WindowContext';
import { testIds } from '../../lib/testIds';
import { NipeChatPanel } from './NipeChatPanel';
// FUTURE-REVIEW: FirstVisitOverlay disabled. To re-enable, restore:
//   import { FirstVisitOverlay } from './FirstVisitOverlay';
// + the handleFirstVisitDismiss callback + the firstVisitShown selector
// + uncomment the JSX mount further down. Components stay on disk.
import { useNipeControlRegistrar } from './NipeControlContext';
import type { WindowSnapshot } from '../../lib/nipe/windowContext';
import { useCurrentAttention } from '../../lib/attention/useAttention';
import { FIRST_RUN_TOUR_ATTENTION_ID } from '../../lib/tour/useFirstRunTour';

// Greet-bubble copy + timing — surfaced 5000ms after the sprite mounts
// so the user has time to orient before NIPE invites a conversation.
// Shown once per browser session (sessionStorage gate). Auto-dismisses
// after GREET_BUBBLE_HOLD_MS so it doesn't camp on screen.
const GREET_BUBBLE_TEXT =
  "Hey! Come talk to me, I'm not one of those useless chatbots.";
const GREET_BUBBLE_DELAY_MS = 5000;
const GREET_BUBBLE_HOLD_MS = 12_000;
const GREET_BUBBLE_SESSION_KEY = 'repancy-nipe-greet-bubble-shown';

// First-click commentary: when a desktop icon is clicked the first
// time, the desktop dispatches `FIRST_CLICK_EVENT` with the commentary
// text. NipeOverlay mounts a NipeIntroBalloon with that text and auto-
// dismisses after FIRST_CLICK_HOLD_MS or on the next click.
export const FIRST_CLICK_EVENT = 'repancy-nipe-first-click';
export interface FirstClickEventDetail {
  text: string;
  /**
   * F18.G.1 — When true, the balloon stays mounted until the user
   * explicitly clicks the close affordance (or opens the NIPE chat
   * panel). Skips the 10s auto-dismiss and the next-document-click
   * dismissal. Used for the portal-welcome message, which is longer
   * than typical first-click commentary and worth keeping visible.
   */
  sticky?: boolean;
}
const FIRST_CLICK_HOLD_MS = 10_000;

/**
 * NIPE — the floating Office-Assistant overlay. Renders a Clippy sprite
 * anchored bottom-right above the Win95 taskbar when the portal flag
 * is on. NOT a window — does not call registerWindow().
 *
 * C.12 cut: sprite + chat panel + first-visit overlay + Convex-backed
 * hide/show prefs + imperative control surface for Start menu / tray.
 */
export function NipeOverlay(): ReactElement | null {
  const flags = useFeatureFlags();
  // 2026-05-28 — useResolvedPortalSlug pulls from React context with
  // URL fallback so the overlay sees the portal even when rendered
  // by the OUTER WindowLayer (outside TenantBrandingProvider).
  const resolvedSlug = useResolvedPortalSlug();
  const [isDemo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();
  const { role } = useRepancyRole();
  const isOperator = role === 'operator';
  const winCtx = useWindowContext();
  const [panelOpen, setPanelOpen] = useState(false);
  const registerControl = useNipeControlRegistrar();
  // 5000ms greet bubble — mounts the same NipeIntroBalloon used when
  // the panel opens, just with the greet copy and without the
  // intro→nudge timer. SessionStorage gates re-fire across navigations
  // in the same tab.
  const [greetVisible, setGreetVisible] = useState(false);
  const greetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const greetDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // First-click commentary balloon — fires when DesktopIcon dispatches
  // FIRST_CLICK_EVENT. Auto-dismisses after FIRST_CLICK_HOLD_MS or on
  // the next click anywhere in the document. Sticky variant (F18.G.1
  // portal-welcome) skips both auto-dismiss paths and waits for an
  // explicit close.
  const [firstClickText, setFirstClickText] = useState<string | null>(null);
  const [firstClickSticky, setFirstClickSticky] = useState<boolean>(false);
  const firstClickHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // NIPE renders on every route. When the user isn't inside a portal,
  // fall back to a synthetic `clientSlug = 'default'` so the sprite
  // mounts. Convex queries are still skipped if neither slug nor demo
  // session is present (so the prefs table stays per-portal in prod).
  const clientSlug = resolvedSlug ?? 'default';
  const hasPortalScope = Boolean(resolvedSlug);

  // Convex-backed prefs (paired demo per `feedback_paired_audit_tables`).
  // Only fetch prefs when inside a real portal or demo session — the
  // 'default' synthetic slug has no Convex prefs row.
  const prefs = useQuery(
    api.nipePrefs.getPrefs,
    flags.nipeEnabled && hasPortalScope
      ? { clientSlug, ...(isDemo ? { demoSessionId: showcaseSessionId } : {}) }
      : 'skip',
  );
  // FUTURE-REVIEW: setPrefs mutation removed with the FirstVisitOverlay
  // mount. Restore `const setPrefs = useMutation(api.nipePrefs.setPrefs);`
  // when handleFirstVisitDismiss returns.
  const logEvent = useMutation(api.nipeAuditLog.logEvent);

  const snapshots = useMemo<WindowSnapshot[]>(
    () =>
      winCtx.windows.map((w) => ({
        path: w.path,
        title: w.title,
        state: w.state,
        zIndex: w.zIndex,
      })),
    [winCtx.windows],
  );

  // Imperative control surface for Start menu + tray icon.
  useEffect(() => {
    registerControl({
      open: () => setPanelOpen(true),
      close: () => setPanelOpen(false),
      toggle: () => setPanelOpen((v) => !v),
    });
  }, [registerControl]);

  // Audit panel open. Fires whenever panelOpen transitions to true.
  // Per `feedback_paired_audit_tables`, paired demo via showcaseSessionId.
  // Skip audit when outside a real portal (the synthetic 'default'
  // slug doesn't correspond to a Convex tenant row).
  useEffect(() => {
    if (!panelOpen) return;
    if (!hasPortalScope) return;
    void logEvent({
      clientSlug,
      event: 'open',
      ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
    }).catch(() => undefined);
  }, [panelOpen, hasPortalScope, clientSlug, isDemo, showcaseSessionId, logEvent]);

  // FUTURE-REVIEW: handleFirstVisitDismiss callback removed alongside
  // the FirstVisitOverlay mount. setPrefs({ firstVisitShown }) +
  // logEvent('first-visit-shown') still work via the mutation hooks; if
  // FirstVisitOverlay returns, restore this callback as the onDismiss /
  // onOpenChat handler.

  /**
   * 5000ms greet-bubble scheduler — schedules a single mount of
   * `<NipeIntroBalloon overrideText=GREET_BUBBLE_TEXT>` after
   * GREET_BUBBLE_DELAY_MS. SessionStorage gates re-fire across
   * navigations in the same tab so the bubble doesn't spam.
   *
   * Skipped when the user has hidden the sprite via prefs, when the
   * chat panel is already open, or when a prior session already saw
   * the bubble.
   */
  // First-run tour gate: while the tour is actively running, defer
  // the greet bubble. Without this gate, the bubble fires 5s after
  // the desktop mounts — which lands mid-tour, distracting from the
  // step-by-step narrative. We re-trigger the scheduler when the
  // tour ends (currentAttention transitions away from first-run-tour),
  // giving the user the 5s orientation budget AFTER the tour wraps.
  const currentAttention = useCurrentAttention();
  const tourActive = currentAttention === FIRST_RUN_TOUR_ATTENTION_ID;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hiddenNow = prefs?.hidden ?? false;
    if (hiddenNow) return;
    if (panelOpen) return;
    if (tourActive) return;
    let alreadyShown = false;
    try {
      alreadyShown =
        window.sessionStorage.getItem(GREET_BUBBLE_SESSION_KEY) === '1';
    } catch {
      /* sessionStorage unavailable — fall through and show */
    }
    if (alreadyShown) return;
    if (greetTimerRef.current !== null) clearTimeout(greetTimerRef.current);
    greetTimerRef.current = setTimeout(() => {
      setGreetVisible(true);
      try {
        window.sessionStorage.setItem(GREET_BUBBLE_SESSION_KEY, '1');
      } catch {
        /* sessionStorage write failure — ignore */
      }
      // Auto-dismiss after GREET_BUBBLE_HOLD_MS so the user isn't
      // permanently nagged. Click on the sprite (which opens the
      // panel) also hides it — see the panelOpen branch in render.
      if (greetDismissTimerRef.current !== null)
        clearTimeout(greetDismissTimerRef.current);
      greetDismissTimerRef.current = setTimeout(
        () => setGreetVisible(false),
        GREET_BUBBLE_HOLD_MS,
      );
    }, GREET_BUBBLE_DELAY_MS);
    return () => {
      if (greetTimerRef.current !== null) {
        clearTimeout(greetTimerRef.current);
        greetTimerRef.current = null;
      }
      if (greetDismissTimerRef.current !== null) {
        clearTimeout(greetDismissTimerRef.current);
        greetDismissTimerRef.current = null;
      }
    };
  }, [prefs?.hidden, panelOpen, tourActive]);

  // When the user opens the chat panel, also dismiss the greet bubble.
  useEffect(() => {
    if (panelOpen && greetVisible) setGreetVisible(false);
  }, [panelOpen, greetVisible]);

  // First-click commentary listener — DesktopIcon (and Start menu)
  // dispatch FIRST_CLICK_EVENT with the icon's commentary on first
  // click; we mount a NipeIntroBalloon with that text. Auto-dismiss
  // after FIRST_CLICK_HOLD_MS, or on the next document click.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (ev: Event): void => {
      const detail = (ev as CustomEvent<FirstClickEventDetail>).detail;
      if (!detail || typeof detail.text !== 'string') return;
      if (firstClickHoldTimerRef.current !== null) {
        clearTimeout(firstClickHoldTimerRef.current);
        firstClickHoldTimerRef.current = null;
      }
      const sticky = detail.sticky === true;
      setFirstClickText(detail.text);
      setFirstClickSticky(sticky);
      if (!sticky) {
        firstClickHoldTimerRef.current = setTimeout(() => {
          setFirstClickText(null);
          setFirstClickSticky(false);
          firstClickHoldTimerRef.current = null;
        }, FIRST_CLICK_HOLD_MS);
      }
    };
    document.addEventListener(FIRST_CLICK_EVENT, handler);
    return () => {
      document.removeEventListener(FIRST_CLICK_EVENT, handler);
    };
  }, []);

  // Dismiss on next click anywhere (after the balloon has rendered
  // for at least one tick so the originating click doesn't dismiss
  // it instantly). Sticky balloons skip this — they require an
  // explicit close from their own UI.
  useEffect(() => {
    if (firstClickText === null) return;
    if (firstClickSticky) return;
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 200);
    const onDocClick = (): void => {
      if (!armed) return;
      setFirstClickText(null);
      setFirstClickSticky(false);
      if (firstClickHoldTimerRef.current !== null) {
        clearTimeout(firstClickHoldTimerRef.current);
        firstClickHoldTimerRef.current = null;
      }
    };
    document.addEventListener('click', onDocClick);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('click', onDocClick);
    };
  }, [firstClickText, firstClickSticky]);

  // When the user opens the NIPE chat panel, dismiss the sticky welcome
  // balloon — they got the message and now they're moving on.
  useEffect(() => {
    if (panelOpen && firstClickText !== null && firstClickSticky) {
      setFirstClickText(null);
      setFirstClickSticky(false);
    }
  }, [panelOpen, firstClickText, firstClickSticky]);

  // NIPE renders on every route (portal or root). The portal-scoped
  // feature flag still hides it inside a portal where the operator
  // turned it off; outside a portal (no branding) we always show.
  if (hasPortalScope && !flags.nipeEnabled) return null;

  const hidden = prefs?.hidden ?? false;
  // FUTURE-REVIEW: firstVisitShown selector removed alongside the
  // FirstVisitOverlay mount. The prefs row still carries the field;
  // restore `const firstVisitShown = prefs?.firstVisitShown ?? true;`
  // if the overlay returns.

  // Portal chat panel + first-visit overlay through document.body so
  // they escape the outer Win95Desktop's `display:none` Outlet wrapper
  // (the portal route renders an inner Win95Desktop inside that
  // wrapper to get TenantBrandingContext in scope; without the portal
  // its DOM would be invisible).
  const body = typeof document !== 'undefined' ? document.body : null;

  return (
    <div data-testid={testIds.nipe.root}>
      <ClippyHost
        hidden={hidden}
        idleDisabled
        soundEnabled={prefs?.soundEnabled ?? false}
        onClick={() => setPanelOpen((v) => !v)}
        onDoubleClick={() => setPanelOpen(true)}
      />
      {/* 5000ms greet bubble — uses the same NipeIntroBalloon styling
          as the panel-open intro / shy nudge so the bubble looks
          identical across all NIPE surfaces. */}
      {greetVisible && !hidden && !panelOpen ? (
        <NipeIntroBalloon
          overrideText={GREET_BUBBLE_TEXT}
          testId={testIds.nipe.greetBubble}
        />
      ) : null}
      {/* First-click commentary balloon. Same styling; takes
          precedence over the greet bubble (the greet bubble auto-
          dismisses anyway, and we don't want both on screen). */}
      {firstClickText !== null && !hidden && !panelOpen ? (
        <NipeIntroBalloon
          overrideText={firstClickText}
          testId="nipe-first-click-balloon"
          onClose={
            firstClickSticky
              ? () => {
                  setFirstClickText(null);
                  setFirstClickSticky(false);
                  if (firstClickHoldTimerRef.current !== null) {
                    clearTimeout(firstClickHoldTimerRef.current);
                    firstClickHoldTimerRef.current = null;
                  }
                }
              : undefined
          }
        />
      ) : null}
      {body && panelOpen && !hidden
        ? createPortal(
            <NipeChatPanel
              clientSlug={clientSlug}
              isOperator={isOperator}
              windows={snapshots}
              onClose={() => setPanelOpen(false)}
            />,
            body,
          )
        : null}
      {/* FUTURE-REVIEW: FirstVisitOverlay disabled. It was the pulsing
          icon + bubble tour shown 5000ms after first load; never
          actually surfaced to most users. Keep on disk; FirstRunTour
          (Phase 6, src/components/tour/) covers the spotlight role now.
      {body && !firstVisitShown && !hidden
        ? createPortal(
            <FirstVisitOverlay
              isOperator={isOperator}
              onDismiss={() => handleFirstVisitDismiss(false)}
              onOpenChat={() => handleFirstVisitDismiss(true)}
            />,
            body,
          )
        : null}
      */}
    </div>
  );
}
